import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { Toaster } from 'sonner';
import PageMorph from './components/PageMorph';
import { AuthProvider } from './context/AuthContext';
import DevRolePreviewBar from './components/DevRolePreviewBar';
import { ThemeProvider } from './context/ThemeContext';
import { TextFormattingProvider } from './context/TextFormattingContext';
import { CommandPaletteProvider } from './context/CommandPaletteContext';
import { SocketProvider } from './context/SocketContext';
import { ChatProvider } from './context/ChatContext';
import { NotificationProvider } from './context/NotificationContext';
import { NotificationCenterProvider } from './context/NotificationCenterContext';
import { WheelNavigationProvider } from './context/WheelNavigationContext';
import { WorkspaceNavigationProvider } from './context/WorkspaceNavigationContext';
import { TemplatesNavigationProvider } from './context/TemplatesNavigationContext';
import WheelNavigationWrapper from './components/WheelNavigationWrapper';
import WorkspaceNavigation from './components/WorkspaceNavigation';
import TemplatesNavigation from './components/TemplatesNavigation';
import { MinimizedTicketProvider } from './context/MinimizedTicketContext';
import { ZenMoveProvider } from './context/ZenMoveContext';
import { SpotlightProvider } from './context/SpotlightContext';
import { AdvancedQAViewProvider } from './context/AdvancedQAViewContext';
import { LinkedGradeProvider } from './context/LinkedGradeContext';
import MinimizedTicketDock from './components/MinimizedTicketDock';
import MinimizeWarpAnimation from './components/MinimizeWarpAnimation';
import AcpQuickView from './components/AcpQuickView';
import ToolsQuickSearch from './components/ToolsQuickSearch';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import PrivateRoute from './components/PrivateRoute';
import PageLayout from './components/PageLayout';

// Login/auth keep eager - first contact pages, small footprint
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';

// All other routes lazy-loaded for per-route code splitting.
// Initial bundle drops dramatically; each route is fetched on demand.
const HomePage = lazy(() => import('./pages/HomePage'));
const Workspace = lazy(() => import('./pages/Workspace'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CountriesRestrictions = lazy(() => import('./pages/CountriesRestrictions'));
const Chat = lazy(() => import('./pages/Chat'));
const VIPProgressCalculator = lazy(() => import('./pages/VIPProgressCalculator'));
const ToolsHome = lazy(() => import('./pages/tools/ToolsHome'));
const ToolBuilderPage = lazy(() => import('./pages/tools/ToolBuilderPage'));
const UserToolPage = lazy(() => import('./pages/tools/UserToolPage'));
const AcpWorkbench = lazy(() => import('./pages/acp/AcpWorkbench'));
const ClaraAI = lazy(() => import('./pages/clara-ai/ClaraAI'));
const HashExplorerFinder = lazy(() => import('./pages/HashExplorerFinder'));
const QuickLinks = lazy(() => import('./pages/QuickLinks'));
const ActiveBets = lazy(() => import('./pages/ActiveBets'));
const CrashBetCheck = lazy(() => import('./pages/CrashBetCheck'));
const BonusChecker = lazy(() => import('./pages/BonusChecker'));
const LadderChecker = lazy(() => import('./pages/LadderChecker'));
const RtpCheck = lazy(() => import('./pages/RtpCheck'));
const Optimove = lazy(() => import('./pages/Optimove'));
const BalanceCalculator = lazy(() => import('./pages/BalanceCalculator'));
const KYC = lazy(() => import('./pages/KYC'));
const TimeConverter = lazy(() => import('./pages/TimeConverter'));
const BetArchiveSearch = lazy(() => import('./pages/BetArchiveSearch'));
const LimitLookup = lazy(() => import('./pages/LimitLookup'));
const AffiliateCommission = lazy(() => import('./pages/AffiliateCommission'));
const BirthdayBonus = lazy(() => import('./pages/BirthdayBonus'));
const HackedAccounts = lazy(() => import('./pages/HackedAccounts'));
const BonusAudit = lazy(() => import('./pages/BonusAudit'));
const WelcomeOfferChecker = lazy(() => import('./pages/WelcomeOfferChecker'));
const DeveloperDashboard = lazy(() => import('./pages/DeveloperDashboard'));
const ActiveIssues = lazy(() => import('./pages/ActiveIssues'));
const AdminHistory = lazy(() => import('./pages/AdminHistory'));
const Goals = lazy(() => import('./pages/Goals'));
const VipHostMetrics = lazy(() => import('./pages/VipHostMetrics'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const AmongGradesPage = lazy(() => import('./components/AmongGrades/AmongGradesPage'));
const KYCGoalsRouter = lazy(() => import('./pages/KYCGoalsRouter'));
const KYCGoalsLayout = lazy(() => import('./pages/KYCGoalsLayout'));
const KYCActivityFeed = lazy(() => import('./pages/KYCActivityFeed'));
const KYCAgentDetail = lazy(() => import('./pages/KYCAgentDetail'));
const KYCChannelDetail = lazy(() => import('./pages/KYCChannelDetail'));
const KYCMetrics = lazy(() => import('./pages/KYCMetrics'));
const KYCStats = lazy(() => import('./pages/KYCStats'));
const ScoresPage = lazy(() => import('./pages/scores/ScoresPage'));
const LeaderboardPage = lazy(() => import('./pages/scores/LeaderboardPage'));
const ScoresCoachingPage = lazy(() => import('./pages/scores/ScoresCoachingPage'));
const ScoresReportsPage = lazy(() => import('./pages/scores/ScoresReportsPage'));
const Monitoring = lazy(() => import('./pages/Monitoring'));
const ClaraAiDashboard = lazy(() => import('./pages/ClaraAiDashboard'));
const PaymentsGoalsRouter = lazy(() => import('./pages/PaymentsGoalsRouter'));
const PaymentsGoalsLayout = lazy(() => import('./pages/PaymentsGoalsLayout'));
const CryptoGoals = lazy(() => import('./pages/CryptoGoals'));
const PaymentsActivityFeed = lazy(() => import('./pages/PaymentsActivityFeed'));
const PaymentsAgentDetail = lazy(() => import('./pages/PaymentsAgentDetail'));
const PaymentsChannelDetail = lazy(() => import('./pages/PaymentsChannelDetail'));

// Everything & Anything — card hub; each card is individually access-gated (admin-managed allow-lists)
const EverythingHome = lazy(() => import('./pages/everything/EverythingHome'));
const ProductionFlagTracker = lazy(() => import('./pages/everything/ProductionFlagTracker'));

// QA Manager - lazy load layout + each sub-page as its own chunk.
// `webpackPrefetch: true` adds <link rel="prefetch"> hints so the browser fetches
// these chunks during idle time after first paint. Inside qa-manager users tend
// to switch tabs frequently, so prefetching keeps navigation feeling instant
// (otherwise each tab click triggers a network fetch + Suspense fallback flash).
const QAManagerLayout = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAManagerLayout'));
const QADashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QADashboard'));
const QAAgents = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAgents'));
const QATickets = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QATickets'));
const QAArchive = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAArchive'));
const QAReview = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAReview'));
const QAReviewAnalytics = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAReviewAnalytics'));
const QAReviewHistory = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAReviewHistory'));
const QAAppeals = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAppeals'));
const QAReport = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAReport'));
const QARelayDashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QARelayDashboard'));
const ACPPreview = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/ACPPreview'));
const QAAIKnowledge = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAIKnowledge'));
const QAAIPicks = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAIPicks'));
const QAAITest = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAITest'));
const QAAIOrchestrator = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAIOrchestrator'));
const QAAIReports = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAAIReports'));
const QALogs = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QALogs'));
const QAScores = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAScores'));
const QAExport = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAExport'));
const QABugsPage = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QABugsPage'));
const QACoaching = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QACoaching'));
const QACoachingDetail = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QACoachingDetail'));
const QACalibration = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QACalibration'));
const QASummaries = lazy(() => import(/* webpackPrefetch: true */ './components/QASummaries'));
const QAWeeklyReport = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/QAWeeklyReport'));
const QAImportTickets = lazy(() => import(/* webpackPrefetch: true */ './components/QAImportTickets'));
const QAAllAgents = lazy(() => import(/* webpackPrefetch: true */ './components/QAAllAgents'));
const StatisticsPage = lazy(() => import(/* webpackPrefetch: true */ './components/statistics/StatisticsPage'));
const QAActiveOverview = lazy(() => import(/* webpackPrefetch: true */ './pages/qa-manager/active-overview'));

// Knowledge Base
const KnowledgeBaseLayout = lazy(() => import('./pages/knowledge-base/KnowledgeBaseLayout'));
const KBHome = lazy(() => import('./pages/knowledge-base/KBHome'));
const KBSearchResults = lazy(() => import('./pages/knowledge-base/KBSearchResults'));
const KBSectionView = lazy(() => import('./pages/knowledge-base/KBSectionView'));
const KBPageView = lazy(() => import('./pages/knowledge-base/KBPageView'));
const KBAdmin = lazy(() => import('./pages/knowledge-base/KBAdmin'));
const KBLearn = lazy(() => import('./pages/knowledge-base/KBLearn'));
const KBAccessControl = lazy(() => import('./pages/knowledge-base/KBAccessControl'));
// DEV-ONLY: KB layout harness (fixtures through the real KBPageView, no auth)
const KBLayoutLab = process.env.NODE_ENV === 'development'
  ? lazy(() => import('./pages/dev/KBLayoutLab'))
  : null;

// TL
const TLLayout = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLLayout'));
const TLDashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLDashboard'));
const TLHome = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/home/TLHome'));
const TLTeamDetail = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLTeamDetail'));
const TLAgentDetail = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLAgentDetail'));
const TLAdmin = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLAdmin'));
const TLReports = lazy(() => import('./pages/tl/TLReports'));
const TLTemplatesList = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLTemplatesList'));
const TLTemplateEditor = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLTemplateEditor'));
const TLTemplateView = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLTemplateView'));
const TLWeeklyReport = lazy(() => import(/* webpackPrefetch: true */ './pages/tl/TLWeeklyReport'));
// Create Team (admin-only tab) — not prefetched: most TL visitors never see it.
const TLCustomTeams = lazy(() => import('./pages/tl/TLCustomTeams'));
const TLCustomTeamBuilder = lazy(() => import('./pages/tl/TLCustomTeamBuilder'));
const TLCustomTeamDetail = lazy(() => import('./pages/tl/TLCustomTeamDetail'));
const TLQaGraders = lazy(() => import('./pages/tl/TLQaGraders'));
const TLAppeals = lazy(() => import('./pages/tl/TLAppeals'));
const TLTickets = lazy(() => import('./pages/tl/TLTickets'));
const TLSelfAssigned = lazy(() => import('./pages/tl/TLSelfAssigned'));
// Presence (/tl/presence) — the sub-layout and its views share one chunk.
const TLPresence = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/TLPresence'));
const PresenceOverview = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceOverview'));
const PresenceDay = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceDay'));
const PresenceBreaks = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceBreaks'));
const PresenceData = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceData'));
const PresenceBreakRules = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceBreakRules'));
const PresenceAgent = lazy(() => import(/* webpackChunkName: "tl-presence" */ './pages/tl/presence/PresenceAgent'));

// Lightweight fallback during chunk fetch — no full-screen flash
const RouteFallback = () => (
  <div className="min-h-[200px] flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-neutral-300 dark:border-neutral-600 border-t-transparent animate-spin" />
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        {/* Local-dev role preview control. Renders null in any production build
            and off localhost; sits directly under AuthProvider so no role gate
            below can hide the way out of a preview. */}
        <DevRolePreviewBar />
        <AdvancedQAViewProvider>
        <SocketProvider>
          <NotificationProvider>
            {/* Consumes both SocketProvider (notification:new) and NotificationProvider
                (showNotification), so it must sit inside both */}
            <NotificationCenterProvider>
            <ChatProvider>
              <WheelNavigationProvider>
                <WorkspaceNavigationProvider>
                  <TemplatesNavigationProvider>
                    <CommandPaletteProvider>
                      <TextFormattingProvider>
                        <Router>
                          <MotionConfig reducedMotion="user">
                          <MaintenanceOverlay />
                          <PageMorph />
                          <SpotlightProvider>
                          <MinimizedTicketProvider>
                          <ZenMoveProvider>
                          {/* One drawer for every $<id> ticket link in the app.
                              Inside <Router> because it reads the route to know
                              which family of endpoints this page may read —
                              see context/LinkedGradeContext.jsx. */}
                          <LinkedGradeProvider>
                          <WheelNavigationWrapper />
                          <WorkspaceNavigation />
                          <TemplatesNavigation />
                          <MinimizedTicketDock />
                          <MinimizeWarpAnimation />
                          <AcpQuickView />
                          <ToolsQuickSearch />
                        <Suspense fallback={<RouteFallback />}>
                        <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Navigate to="/home" replace />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        {KBLayoutLab && <Route path="/kb-layout-lab" element={<KBLayoutLab />} />}
                        {KBLayoutLab && <Route path="/kb-layout-lab/:slug" element={<KBLayoutLab />} />}
                        <Route path="/active-issues" element={<PrivateRoute><ActiveIssues /></PrivateRoute>} />

                        {/* Home page */}
                        <Route
                          path="/home"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="home">
                                <HomePage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Tools routes - grouped under /tools. A tool that searches a user takes an
                            optional /:userId — its shareable link (utils/dockEmbed.js useDockAcpUser reads
                            it on arrival and writes it after each search; one route, so no remount). */}
                        <Route
                          path="/tools/vip-calculator/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="vip-calculator">
                                <VIPProgressCalculator />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/hash-explorer"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="hash-explorer">
                                <HashExplorerFinder />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/active-bets/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="active-bets">
                                <ActiveBets />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/crash-bet-check"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="crash-bet-check">
                                <CrashBetCheck />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/bonus-checker/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="bonus-checker">
                                <BonusChecker />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/clara-ai/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="clara-ai">
                                <ClaraAI />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/optimove/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="optimove">
                                <Optimove />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/balance-calculator/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="balance-calculator">
                                <BalanceCalculator />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/bet-archive-search/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="bet-archive-search">
                                <BetArchiveSearch />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/limit-lookup/:userId?"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer']}>
                              <PageLayout activeSection="tools" currentTool="limit-lookup">
                                <LimitLookup />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/affiliate-commission/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="affiliate-commission">
                                <AffiliateCommission />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/birthday-bonus/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="birthday-bonus">
                                <BirthdayBonus />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/hacked-accounts/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="hacked-accounts">
                                <HackedAccounts />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/audit"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer', 'audit']}>
                              <PageLayout activeSection="tools" currentTool="audit">
                                <BonusAudit />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Ladder Checker (CRM team, 2026-09-22): the ladder thresholds are internal, so only
                            crm + admin/developer — API twin routes/ladderRoutes.js LADDER_ROLES. */}
                        <Route
                          path="/tools/ladder-checker/:userId?"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer', 'crm']}>
                              <PageLayout activeSection="tools" currentTool="ladder-checker">
                                <LadderChecker />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/rtp-check/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="rtp-check">
                                <RtpCheck />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/welcome-offer-checker/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="welcome-offer-checker">
                                <WelcomeOfferChecker />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/quick-links"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="quick-links">
                                <QuickLinks />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/kyc"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="kyc">
                                <KYC />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/time-converter"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="time-converter">
                                <TimeConverter />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/tools/countries-restrictions"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools" currentTool="countries-restrictions">
                                <CountriesRestrictions />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Tool Builder (2026-09-17, LOCAL) — agents build tools with Clara
                            from a procedure + ACP fields; saved tools run on /tools/my/:id and
                            as ACP dock tabs. All calls go to /api/user-tools (login required). */}
                        <Route
                          path="/tools/builder"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools">
                                <ToolBuilderPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/tools/builder/:id"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools">
                                <ToolBuilderPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/tools/my/:id/:userId?"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools">
                                <UserToolPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Tools dashboard — landing page for the /tools section */}
                        <Route
                          path="/tools"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tools">
                                <ToolsHome />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* ACP — the standalone workbench (2026-09-16): the QA Manager ACP
                            panel as a page plus the tools dock. Access is the API verdict
                            (/api/auth/acp-access — login email ↔ stake.com ACP support role);
                            the page renders that verdict itself, so no requiredRoles here. */}
                        <Route
                          path="/acp"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="acp">
                                <AcpWorkbench />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />


                        {/* Backward compatibility redirects for old URLs */}
                        <Route path="/vip-calculator" element={<Navigate to="/tools/vip-calculator" replace />} />
                        <Route path="/hash-explorer" element={<Navigate to="/tools/hash-explorer" replace />} />
                        <Route path="/quick-links" element={<Navigate to="/tools/quick-links" replace />} />
                        <Route path="/kyc" element={<Navigate to="/tools/kyc" replace />} />
                        <Route path="/countries-restrictions" element={<Navigate to="/tools/countries-restrictions" replace />} />

                        <Route
                          path="/developer-dashboard"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer']}>
                              <PageLayout activeSection="developer-dashboard">
                                <DeveloperDashboard />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/admin/history"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="admin-history">
                                <AdminHistory />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/goals"
                          element={
                            <PrivateRoute>
                              <Goals />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/vip-host-metrics"
                          element={
                            <PrivateRoute requiredRoles={['vip-host-tl', 'admin', 'developer']}>
                              <VipHostMetrics />
                            </PrivateRoute>
                          }
                        />

                        {/* User Management — UserManagementPage renders its own PageLayout
                            (like /goals and /vip-host-metrics), so we don't wrap it again here.
                            TEMP (2026-06-16): ADMIN-ONLY while testing. Restore the full set when done:
                            ['admin', 'developer', 'qa-admin', 'host-qa-admin', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'tl', 'vip-host-tl', 'kyc-admin'].
                            Keep in sync with backend auth.js `userMgmt` guard + AppSidebar roleAccess. */}
                        <Route
                          path="/user-management"
                          element={
                            <PrivateRoute requiredRoles={['admin']}>
                              <UserManagementPage />
                            </PrivateRoute>
                          }
                        />

                        {/* QA Manager routes with nested routing */}
                        <Route
                          path="/qa-manager"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="qa-manager">
                                <QAManagerLayout />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        >
                          {/* Default redirect to dashboard */}
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard" element={<QADashboard />} />
                          <Route path="agents" element={<QAAgents />} />
                          <Route path="tickets" element={<QATickets />} />
                          <Route path="tickets/:ticketId" element={<QATickets />} />
                          <Route path="tickets/:ticketId/edit" element={<QATickets />} />
                          <Route path="archive" element={<QAArchive />} />
                          <Route path="archive/:ticketId" element={<QAArchive />} />
                          <Route path="archive/:ticketId/edit" element={<QAArchive />} />
                          <Route path="review" element={<QAReview />} />
                          <Route path="review/analytics" element={<QAReviewAnalytics />} />
                          <Route path="review/history" element={<QAReviewHistory />} />
                          <Route path="review/history/:ticketId" element={<QAReviewHistory />} />
                          <Route path="review/history/:ticketId/edit" element={<QAReviewHistory />} />
                          <Route path="review/:ticketId" element={<QAReview />} />
                          <Route path="review/:ticketId/edit" element={<QAReview />} />
                          <Route path="appeals" element={<QAAppeals />} />
                          <Route path="appeals/:appealId" element={<QAAppeals />} />
                          <Route path="summaries" element={<QASummaries />} />
                          <Route path="weekly-report" element={<QAWeeklyReport />} />
                          <Route path="coaching" element={<QACoaching />} />
                          <Route path="coaching/:id" element={<QACoachingDetail />} />
                          <Route path="calibration" element={<QACalibration />} />
                          <Route path="calibration/:sessionId" element={<QACalibration />} />
                          <Route path="all-agents" element={<QAAllAgents />} />
                          <Route path="qa-logs" element={<QALogs />} />
                          <Route path="statistics" element={<StatisticsPage />} />
                          <Route path="acp-relay" element={<QARelayDashboard />} />
                          <Route path="acp-preview" element={<ACPPreview />} />
                          <Route path="import-tickets" element={<QAImportTickets />} />
                          <Route path="active-overview" element={<QAActiveOverview />} />
                          <Route path="active-overview/agents/:id" element={<QAActiveOverview />} />
                          <Route path="bugs" element={<QABugsPage />} />
                          <Route path="report" element={<QAReport />} />
                          <Route path="among-grades" element={<AmongGradesPage />} />
                          <Route path="ai-knowledge" element={
                            <PrivateRoute requiredRoles={['qa-admin', 'host-qa-admin', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'admin']}>
                              <QAAIKnowledge />
                            </PrivateRoute>
                          } />
                          <Route path="ai-picks" element={<QAAIPicks />} />
                          <Route path="ai-reports" element={<QAAIReports />} />
                          <Route path="ai-test" element={<QAAITest />} />
                          <Route path="ai-orchestrator" element={
                            <PrivateRoute requiredRoles={['qa-admin', 'host-qa-admin', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'admin']} requiredCap="ai-hub">
                              <QAAIOrchestrator />
                            </PrivateRoute>
                          } />
                          <Route path="scores" element={<QAScores />} />
                          <Route path="export" element={
                            <PrivateRoute requiredRoles={['qa-admin', 'host-qa-admin', 'kick-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'admin', 'developer']} requiredCap="export">
                              <QAExport />
                            </PrivateRoute>
                          } />
                        </Route>

                        {/* Knowledge Base routes with nested routing */}
                        <Route
                          path="/knowledge-base"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="knowledge-base">
                                <KnowledgeBaseLayout />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        >
                          <Route index element={<KBHome />} />
                          <Route path="search" element={<KBSearchResults />} />
                          <Route path="admin" element={<KBAdmin />} />
                          <Route path="learn" element={<KBLearn />} />
                          <Route path="access" element={<KBAccessControl />} />
                          <Route path="section/:sectionId" element={<KBSectionView />} />
                          <Route path=":slug" element={<KBPageView />} />
                        </Route>

                        {/* TL (Team Leader) routes with nested routing */}
                        <Route
                          path="/tl"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="tl">
                                <TLLayout />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        >
                          <Route index element={<TLHome />} />
                          <Route path="dashboard" element={<TLDashboard />} />
                          <Route path="team/:teamName" element={<TLTeamDetail />} />
                          <Route path="agent/:agentId" element={<TLAgentDetail />} />
                          <Route path="templates" element={<TLTemplatesList />} />
                          <Route path="templates/new" element={<TLTemplateEditor />} />
                          <Route path="templates/:id" element={<TLTemplateView />} />
                          <Route path="templates/:id/edit" element={<TLTemplateEditor />} />
                          <Route path="templates/:id/agent/:agentId" element={<TLAgentDetail />} />
                          <Route path="weekly-report" element={<TLWeeklyReport />} />
                          <Route path="reports" element={<TLReports />} />
                          {/* Appeals — the list and the deep link to one appeal
                              render the same page; the id opens the grade
                              drawer over the list (TLLayout keys its page
                              transition so this does not remount it). */}
                          <Route path="appeals" element={<TLAppeals />} />
                          <Route path="appeals/:appealId" element={<TLAppeals />} />
                          {/* Ticket explorer — same shape: the id opens the grade
                              drawer over the filtered list, and the list's
                              filters live in the query string. */}
                          <Route path="tickets" element={<TLTickets />} />
                          <Route path="tickets/:ticketDbId" element={<TLTickets />} />
                          {/* Presence — a sub-layout with its own inner navbar
                              (TLLayout keys every /tl/presence/* path as one
                              page); literal segments before agent/:agentId. */}
                          <Route path="presence" element={<TLPresence />}>
                            <Route index element={<PresenceOverview />} />
                            <Route path="day" element={<PresenceDay />} />
                            <Route path="breaks" element={<PresenceBreaks />} />
                            <Route path="data" element={<PresenceData />} />
                            <Route path="break-rules" element={<PresenceBreakRules />} />
                            <Route path="agent/:agentId" element={<PresenceAgent />} />
                          </Route>
                          {/* Self-assigned — conversations agents took by hand while
                              Active; Presence's audience and scope, its own tab. */}
                          <Route path="self-assigned" element={<TLSelfAssigned />} />
                          {/* Custom teams — 'new' before ':id' so the builder
                              route is not swallowed as a team id. */}
                          <Route path="custom-teams" element={<TLCustomTeams />} />
                          <Route path="custom-teams/new" element={<TLCustomTeamBuilder />} />
                          <Route path="custom-teams/:id" element={<TLCustomTeamDetail />} />
                          <Route path="qa-graders" element={<TLQaGraders />} />
                          <Route path="custom-teams/:id/edit" element={<TLCustomTeamBuilder />} />
                          <Route path="admin" element={<TLAdmin />} />
                        </Route>


                        <Route
                          path="/kyc-goals"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="kyc-goals">
                                <KYCGoalsLayout />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        >
                          <Route index element={<KYCGoalsRouter />} />
                          <Route path="activity" element={<KYCActivityFeed />} />
                          {/* Metrics — admin / developer / kyc-admin only (the page
                              redirects everyone else; the API 403s them). */}
                          <Route path="metrics" element={<KYCMetrics />} />
                          <Route path="agent/:agentId" element={<KYCAgentDetail />} />
                          <Route path="channel/:channelId" element={<KYCChannelDetail />} />
                        </Route>

                        {/* kyc-admin added 2026-08-03: manages the Intercom workspace
                            merge groups (keep in sync with BE featureAccess) */}
                        <Route
                          path="/payments-goals"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer', 'payments-admin', 'payments-user', 'payments-qa', 'tl', 'kyc-admin']}>
                              <PageLayout activeSection="payments-goals">
                                <PaymentsGoalsLayout />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        >
                          <Route index element={<PaymentsGoalsRouter />} />
                          <Route path="activity" element={<PaymentsActivityFeed />} />
                          <Route path="agent/:agentId" element={<PaymentsAgentDetail />} />
                          <Route path="channel/:channelId" element={<PaymentsChannelDetail />} />
                        </Route>

                        {/* Crypto Goals (2026-09-10): read-only Intercom figures for the
                            crypto team. admin / developer / crypto-admin see everyone,
                            crypto-user their own row; tl is NOT admitted (keep in sync
                            with BE featureAccess('crypto-goals')). */}
                        <Route
                          path="/crypto-goals"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer', 'crypto-admin', 'crypto-user']}>
                              <PageLayout activeSection="crypto-goals">
                                <CryptoGoals />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Scores — every graded ticket per agent, advanced filtering +
                            analytics. Access is decided at RUNTIME by the backend
                            (MAESTRO_CUTOVER_OFF): admin-only pre-cutover, everyone after.
                            No requiredRoles here on purpose — the page asks
                            /api/scores/my-access and renders a Restricted plate itself,
                            so the cutover flip needs no frontend redeploy. */}
                        <Route
                          path="/scores"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <ScoresPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        {/* One grade / one appeal by URL — the drawer open over
                            the same page. Both render ScoresPage: the id opens
                            the drawer and the search (?agent=, filters) is kept,
                            so the page behind it is the same for whoever opens
                            the link. Who may open it is the drawer's own read
                            (/tickets/:id/insights, /appeals/:id): the agent the
                            grade belongs to, or cross-agent access. */}
                        <Route
                          path="/scores/tickets/:ticketId"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <ScoresPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/scores/appeals/:appealId"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <ScoresPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        {/* Leaderboard — where one agent stands on TPH / FRT /
                            MRT / QA Score within their team, position band and
                            the company. Same runtime access rule as /scores.
                            The board itself carries nobody else's name; only
                            the TEAM placing opens into a named standings list
                            (see utils/leaderboardBoards.js). */}
                        <Route
                          path="/scores/leaderboard"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <LeaderboardPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        {/* Coaching — the agent-facing side of QA coaching
                            sessions. Same runtime access rule as /scores
                            (no requiredRoles; the page probes /my-access). */}
                        <Route
                          path="/scores/coaching"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <ScoresCoachingPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        {/* Reports — the agent's approved monthly/quarterly Q Report
                            documents. Same runtime access rule as /scores. */}
                        <Route
                          path="/scores/reports"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="scores">
                                <ScoresReportsPage />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* KYC Stats — KYC analytics. Admin, developer and the KYC team's
                            admins (kyc-admin, Filip 2026-09-18). Backend twin: middleware/auth.js
                            kycStats; sidebar twin: ROLE_ACCESS['kyc-admin'] in navConfig.js. */}
                        <Route
                          path="/kyc-stats"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer', 'kyc-admin']}>
                              <PageLayout activeSection="kyc-stats">
                                <KYCStats />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Monitoring Center — observability. Admin + developer only. */}
                        <Route
                          path="/monitoring"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer']}>
                              <PageLayout activeSection="monitoring">
                                <Monitoring />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Devs → Clara AI: how agents use Clara, per agent and per conversation (2026-10-24). */}
                        <Route
                          path="/clara-ai-dashboard"
                          element={
                            <PrivateRoute requiredRoles={['admin', 'developer']}>
                              <PageLayout activeSection="clara-ai-dashboard">
                                <ClaraAiDashboard />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Everything & Anything — card hub. Any authed user can open the hub;
                            it only shows the cards the user has been granted (admins see all +
                            manage access). Card sub-pages enforce access server-side (403). */}
                        <Route
                          path="/everything"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="everything">
                                <EverythingHome />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/everything/production-flag-tracker"
                          element={
                            <PrivateRoute>
                              <PageLayout activeSection="everything">
                                <ProductionFlagTracker />
                              </PageLayout>
                            </PrivateRoute>
                          }
                        />


                        {/* Workspace route - ONLY for canvas */}
                        <Route
                          path="/workspace/:workspaceId"
                          element={
                            <PrivateRoute>
                              <Workspace />
                            </PrivateRoute>
                          }
                        />

                        {/* Chat routes */}
                        <Route
                          path="/chat"
                          element={
                            <PrivateRoute>
                              <Chat />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/chat/:channelId"
                          element={
                            <PrivateRoute>
                              <Chat />
                            </PrivateRoute>
                          }
                        />
                      </Routes>
                      </Suspense>
                      <Toaster
                        position="top-center"
                        richColors
                        closeButton
                        theme="system"
                      />
                          </LinkedGradeProvider>
                          </ZenMoveProvider>
                          </MinimizedTicketProvider>
                          </SpotlightProvider>
                          </MotionConfig>
                    </Router>
                      </TextFormattingProvider>
                    </CommandPaletteProvider>
                  </TemplatesNavigationProvider>
                </WorkspaceNavigationProvider>
              </WheelNavigationProvider>
            </ChatProvider>
            </NotificationCenterProvider>
          </NotificationProvider>
        </SocketProvider>
        </AdvancedQAViewProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
